import * as THREE from "three";
import { WORLD } from "../config";
import { gerstnerHeight } from "../ocean";
import { EAST_PONTOON, SOUTH_QUAY, WEST_PONTOON, inHarbourWater, swimLanes } from "./places";

const SKIN = [0xe8beac, 0xc68642, 0x8d5524, 0xf1c27d, 0xffddbf, 0xd4a574];
const HAIR = [0x1a120c, 0x4a2c0a, 0xc4a35a, 0x222222, 0x6b3a2a, 0xe8dcc8, 0x3b2414];
const SHIRT = [0xf2f5f7, 0xe85a24, 0x1b365d, 0x1aa6a1, 0xe6d5a8, 0x3d7a52, 0xf4c542];
const BOTTOMS = [0x2a4050, 0x4a4d50, 0xf2f5f7, 0xc4b18a, 0x1b2429];

export type Kind = "male" | "female" | "child";

type Parts = {
  torso: THREE.Object3D;
  head: THREE.Object3D;
  armL: THREE.Object3D;
  armR: THREE.Object3D;
  legL: THREE.Object3D;
  legR: THREE.Object3D;
};

export class Person {
  readonly group: THREE.Group;
  readonly kind: Kind;
  private parts: Parts;
  private path: THREE.Vector3[];
  private seg = 0;
  private u = 0;
  private speed: number;
  private sit: boolean;
  private swim: boolean;
  private phase: number;
  private heading = 0;

  constructor(kind: Kind, path: THREE.Vector3[], sit = false, seed = 0, swim = false) {
    this.kind = kind;
    this.path = path;
    this.sit = sit;
    this.swim = swim;
    this.phase = seed * 1.7;
    this.speed = swim ? 0.85 : kind === "child" ? 0.95 : 1.25 + (seed % 5) * 0.08;
    this.group = buildHuman(kind, seed, swim);
    this.parts = this.group.userData.parts as Parts;
    if (swim) this.group.rotation.order = "YXZ";
    if (sit) {
      this.parts.legL.rotation.x = 1.18;
      this.parts.legR.rotation.x = 1.22;
      this.parts.torso.rotation.x = 0.18;
      this.parts.armL.rotation.x = 0.55;
      this.parts.armR.rotation.x = 0.4;
      const p = path[0];
      this.group.position.copy(p);
      this.group.rotation.y = path[1] ? Math.atan2(path[1].x - p.x, path[1].z - p.z) : 0;
    } else {
      this.u = (seed * 0.17) % 1;
      this.group.position.copy(path[0]);
    }
  }

  update(t: number, dt: number): void {
    if (this.sit) {
      this.parts.head.rotation.y = Math.sin(t * 0.6 + this.phase) * 0.35;
      this.parts.armR.rotation.x = 0.4 + Math.sin(t * 0.9 + this.phase) * 0.08;
      return;
    }

    const a = this.path[this.seg];
    const b = this.path[(this.seg + 1) % this.path.length];
    const span = a.distanceTo(b) || 1;
    this.u += (this.speed * dt) / span;
    while (this.u >= 1) {
      this.u -= 1;
      this.seg = (this.seg + 1) % this.path.length;
    }
    const from = this.path[this.seg];
    const to = this.path[(this.seg + 1) % this.path.length];
    this.group.position.lerpVectors(from, to, this.u);
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    if (dx * dx + dz * dz > 1e-6) {
      const want = Math.atan2(dx, dz);
      this.heading = THREE.MathUtils.lerp(this.heading, want, 1 - Math.exp(-8 * dt));
      this.group.rotation.y = this.heading;
    }

    if (this.swim) {
      const scale = this.kind === "child" ? 0.62 : this.kind === "female" ? 0.96 : 1;
      const mid = 0.9 * scale;
      this.group.position.x -= Math.sin(this.heading) * mid;
      this.group.position.z -= Math.cos(this.heading) * mid;
      const x = this.group.position.x;
      const z = this.group.position.z;
      this.group.position.y = gerstnerHeight(x, z, t) + 0.05;
      const stroke = t * 5.4 + this.phase;
      this.group.rotation.set(1.48, this.heading, Math.sin(stroke) * 0.18, "YXZ");
      this.parts.armL.rotation.set(stroke, 0, 0.28);
      this.parts.armR.rotation.set(stroke + Math.PI, 0, -0.28);
      const kick = Math.sin(stroke * 2.15);
      this.parts.legL.rotation.set(kick * 0.55, 0, 0.08);
      this.parts.legR.rotation.set(-kick * 0.55, 0, -0.08);
      this.parts.head.rotation.set(-0.2, 0, Math.sin(stroke) * 0.45);
      this.parts.torso.rotation.x = 0.04;
      return;
    }

    if (inHarbourWater(this.group.position.x, this.group.position.z)) {
      this.u = 1;
      return;
    }

    const gait = t * this.speed * 4.2 + this.phase;
    const swing = Math.sin(gait) * 0.72;
    this.parts.legL.rotation.x = swing;
    this.parts.legR.rotation.x = -swing;
    this.parts.armL.rotation.x = -swing * 0.7;
    this.parts.armR.rotation.x = swing * 0.7;
    this.parts.torso.position.y = Math.abs(Math.sin(gait * 2)) * 0.025;
    this.parts.head.rotation.y = Math.sin(t * 0.8 + this.phase) * 0.08;
  }
}

function mat(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness: 0.65, metalness: 0.02 });
}

function buildHuman(kind: Kind, seed: number, swim = false): THREE.Group {
  const g = new THREE.Group();
  const skin = mat(SKIN[seed % SKIN.length]);
  const hairC = mat(HAIR[seed % HAIR.length]);
  const suit = [0xe85a24, 0x1b365d, 0x1aa6a1, 0xf4c542, 0xf2f5f7];
  const shirt = mat(swim ? suit[seed % suit.length] : SHIRT[seed % SHIRT.length]);
  const pants = mat(swim ? suit[(seed + 2) % suit.length] : BOTTOMS[(seed + 2) % BOTTOMS.length]);
  const scale = kind === "child" ? 0.62 : kind === "female" ? 0.96 : 1;
  const shoulder = kind === "male" ? 0.38 : 0.33;
  const hip = kind === "female" ? 0.36 : 0.32;

  const torso = new THREE.Group();
  torso.position.y = 1.12;
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(kind === "female" ? 0.17 : 0.2, 0.42, 3, 8), shirt);
  body.position.y = 0.12;
  body.castShadow = true;
  torso.add(body);
  if (kind === "female") {
    const hipMesh = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), pants);
    hipMesh.position.y = -0.16;
    hipMesh.scale.set(1.15, 0.7, 0.95);
    torso.add(hipMesh);
  }

  const head = new THREE.Group();
  head.position.y = 0.48;
  const skull = new THREE.Mesh(new THREE.SphereGeometry(kind === "child" ? 0.13 : 0.115, 8, 6), skin);
  skull.castShadow = true;
  head.add(skull);
  const hair = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6), hairC);
  hair.position.y = kind === "male" ? 0.05 : 0.04;
  hair.scale.set(1.05, kind === "female" ? 1.15 : 0.7, 1.1);
  head.add(hair);
  if (kind === "female") {
    const fall = new THREE.Mesh(new THREE.SphereGeometry(0.09, 6, 5), hairC);
    fall.position.set(0, -0.06, -0.08);
    fall.scale.set(0.9, 1.4, 0.8);
    head.add(fall);
  }
  torso.add(head);

  const armL = makeArm(skin, shirt, -shoulder);
  const armR = makeArm(skin, shirt, shoulder);
  torso.add(armL, armR);

  const hips = new THREE.Group();
  hips.position.y = 0.9;
  const pelvis = new THREE.Mesh(new THREE.SphereGeometry(hip * 0.55, 8, 6), pants);
  pelvis.scale.set(1.1, 0.55, 0.85);
  hips.add(pelvis);

  const legL = makeLeg(skin, pants, -0.1, swim);
  const legR = makeLeg(skin, pants, 0.1, swim);
  hips.add(legL, legR);

  g.add(hips);
  g.add(torso);
  g.scale.setScalar(scale);
  g.userData.parts = { torso, head, armL, armR, legL, legR };
  return g;
}

function makeArm(skin: THREE.Material, shirt: THREE.Material, x: number): THREE.Group {
  const arm = new THREE.Group();
  arm.position.set(x, 0.28, 0);
  const upper = new THREE.Mesh(new THREE.CapsuleGeometry(0.045, 0.22, 2, 6), shirt);
  upper.position.y = -0.14;
  arm.add(upper);
  const lower = new THREE.Mesh(new THREE.CapsuleGeometry(0.04, 0.2, 2, 6), skin);
  lower.position.y = -0.36;
  arm.add(lower);
  return arm;
}

function makeLeg(skin: THREE.Material, pants: THREE.Material, x: number, swim: boolean): THREE.Group {
  const leg = new THREE.Group();
  leg.position.set(x, 0, 0);
  const thigh = new THREE.Mesh(new THREE.CapsuleGeometry(0.07, 0.32, 2, 6), pants);
  thigh.position.y = -0.22;
  thigh.castShadow = true;
  leg.add(thigh);
  const shin = new THREE.Mesh(new THREE.CapsuleGeometry(0.055, 0.3, 2, 6), skin);
  shin.position.y = -0.58;
  leg.add(shin);
  if (!swim) {
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.05, 0.18), pants);
    foot.position.set(0, -0.78, 0.04);
    leg.add(foot);
  }
  return leg;
}

const QY = WORLD.quayHeight;

function along(poly: Array<[number, number]>, y: number): THREE.Vector3[] {
  const pts = poly.map(([x, z]) => new THREE.Vector3(x, y, z));
  return [...pts, ...[...pts].reverse().slice(1, -1)];
}

function loop(pts: Array<[number, number, number]>): THREE.Vector3[] {
  return pts.map(([x, y, z]) => new THREE.Vector3(x, y, z));
}

export function spawnPeople(): Person[] {
  const people: Person[] = [];
  const paths: THREE.Vector3[][] = [
    along(SOUTH_QUAY, QY),
    along(WEST_PONTOON, QY),
    along(EAST_PONTOON, QY),
    loop([
      [0, 1.86, -80],
      [0, 1.86, -200],
      [48, 1.86, -200],
      [48, 1.86, -88],
      [0, 1.86, -88],
    ]),
    loop([
      [-40, 1.86, -88],
      [40, 1.86, -88],
      [40, 1.86, -168],
      [-40, 1.86, -168],
    ]),
    loop([
      [-12, 1.86, -108],
      [12, 1.86, -108],
      [12, 1.86, -128],
      [-12, 1.86, -128],
    ]),
    loop([
      [-8, 2.17, -32],
      [8, 2.17, -32],
      [10, 2.17, -38],
      [-6, 2.17, -38],
    ]),
  ];

  const kinds: Kind[] = [
    "male",
    "female",
    "child",
    "female",
    "male",
    "female",
    "male",
    "child",
    "female",
    "male",
    "child",
    "female",
    "male",
    "female",
    "male",
    "child",
    "female",
    "male",
    "female",
    "male",
    "child",
    "female",
    "male",
    "female",
  ];

  kinds.forEach((kind, i) => {
    people.push(new Person(kind, paths[i % paths.length], false, i + 3));
  });

  const sitters: Array<[Kind, number, number, number, number]> = [
    ["female", -9, 2.52, -28.4, Math.PI],
    ["male", -5, 2.52, -28.4, Math.PI],
    ["female", 5, 2.52, -28.4, Math.PI],
    ["male", 9, 2.52, -28.4, Math.PI],
    ["child", -12, QY, -30, 0.2],
    ["female", -16, QY, -28, 0.2],
    ["male", 20, QY, -28, Math.PI],
    ["female", -40, QY, 8, Math.PI / 2],
  ];
  sitters.forEach(([kind, x, y, z, yaw], i) => {
    const look = new THREE.Vector3(x + Math.sin(yaw), y, z + Math.cos(yaw));
    const p = new Person(kind, [new THREE.Vector3(x, y, z), look], true, i + 40);
    p.group.rotation.y = yaw;
    people.push(p);
  });

  const lanes = swimLanes();
  const swimKinds: Kind[] = ["male", "female", "male"];
  swimKinds.forEach((kind, i) => {
    const lane = lanes[i % lanes.length].map(([x, z]) => new THREE.Vector3(x, 0, z));
    people.push(new Person(kind, lane, false, i + 70, true));
  });

  return people;
}
